/**
 * MyMouvOrganizerPanel — Full organizer workspace for MyMouv events.
 * Mounted as a tab in ProSpace ("Destine Ads"). Lets Pro users:
 *   - Create/edit events (cover, media, tickets, IBAN, socials, location)
 *   - See their events + tickets sold + revenue breakdown
 *   - Access the ticket scanner
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit3, Trash2, Upload, X, QrCode, Save, Loader2, Ticket, ChevronRight, MapPin, DollarSign, Download, CheckCircle, Info, Tag, XCircle, Briefcase, ShieldCheck, CalendarDays } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MyMouvPromoCodesPanel from './MyMouvPromoCodesPanel';
import EventOperationsModal from './EventOperationsModal';
import EventAccreditationsInbox from './EventAccreditationsInbox';
import { useLanguage } from '../context/LanguageContext';
import { getMediaUrl } from '../utils/mediaUrl';

const CURRENCIES = ['EUR', 'USD', 'GBP', 'CAD', 'XOF', 'XAF', 'JPY', 'CNY', 'INR', 'BRL', 'RUB', 'KRW', 'ARS'];

// v49.4 — 13-language mini dict for the "ticket payout on event day" banner
// + labels for the two new organizer actions (Gestion événement / Accréditations).
const OP_BANNER = {
  fr: { payout: 'Le versement des billets s\u2019effectue le jour de l\u2019événement.', ops: 'Gestion événement', accr: 'Accréditations', allowAccr: 'Autoriser les demandes d\u2019accréditation' },
  en: { payout: 'Ticket payout is processed on the day of the event.', ops: 'Event management', accr: 'Accreditations', allowAccr: 'Allow accreditation requests' },
  es: { payout: 'El pago de las entradas se efectúa el día del evento.', ops: 'Gestión del evento', accr: 'Acreditaciones', allowAccr: 'Permitir solicitudes de acreditación' },
  pt: { payout: 'O pagamento dos bilhetes é feito no dia do evento.', ops: 'Gestão do evento', accr: 'Acreditações', allowAccr: 'Permitir pedidos de acreditação' },
  de: { payout: 'Die Ticketauszahlung erfolgt am Tag der Veranstaltung.', ops: 'Event-Verwaltung', accr: 'Akkreditierungen', allowAccr: 'Akkreditierungsanträge zulassen' },
  it: { payout: 'Il pagamento dei biglietti avviene il giorno dell\u2019evento.', ops: "Gestione dell'evento", accr: 'Accrediti', allowAccr: 'Consentire richieste di accredito' },
  ru: { payout: 'Выплата за билеты производится в день мероприятия.', ops: 'Управление событием', accr: 'Аккредитации', allowAccr: 'Разрешить заявки на аккредитацию' },
  ar: { payout: 'يتم دفع مبالغ التذاكر يوم الحدث.', ops: 'إدارة الحدث', accr: 'الاعتمادات', allowAccr: 'السماح بطلبات الاعتماد' },
  zh: { payout: '门票款项将于活动当日发放。', ops: '活动管理', accr: '认证', allowAccr: '允许认证申请' },
  ja: { payout: 'チケット代金の入金はイベント当日に行われます。', ops: 'イベント管理', accr: '認定', allowAccr: '認定申請を許可' },
  hi: { payout: 'टिकट भुगतान इवेंट के दिन किया जाता है।', ops: 'इवेंट प्रबंधन', accr: 'मान्यताएँ', allowAccr: 'मान्यता अनुरोध की अनुमति दें' },
  nl: { payout: 'De ticketbetaling wordt op de dag van het evenement uitgevoerd.', ops: 'Evenementbeheer', accr: 'Accreditaties', allowAccr: 'Accreditatie-aanvragen toestaan' },
  sw: { payout: 'Malipo ya tikiti hufanywa siku ya tukio.', ops: 'Usimamizi wa tukio', accr: 'Vibali', allowAccr: 'Ruhusu maombi ya kibali' },
  ht: { payout: 'Peman biye yo fèt jou evènman an.', ops: 'Jesyon evènman', accr: 'Akreditasyon', allowAccr: 'Pèmèt demann akreditasyon' },
};

const blankTicket = () => ({ id: 't_' + Math.random().toString(36).slice(2, 8), name: 'Standard', price: 25, currency: 'EUR', quantity: 100, is_vip: false });
const blankEvent = () => ({
  title: '',
  description: '',
  cover_image_url: '',
  images: [],
  video_url: '',
  event_date: '',
  event_time: '20:00',
  location: { name: '', address: '', lat: null, lng: null },
  security_measures: '',
  lineup: [],
  ticket_types: [blankTicket()],
  external_link_title: '',
  external_link_url: '',
  socials: { instagram: '', facebook: '', twitter: '', tiktok: '' },
  is_paid: true,
  status: 'draft',
  allow_accreditations: false,
});

const MyMouvOrganizerPanel = () => {
  const { t, language } = useLanguage();
  const opL = OP_BANNER[language] || OP_BANNER.fr;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [iban, setIban] = useState('');
  const [ibanHolder, setIbanHolder] = useState('');
  const [ibanBusy, setIbanBusy] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | event id
  const [form, setForm] = useState(blankEvent());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [feesOpen, setFeesOpen] = useState(false);
  const [promoEvent, setPromoEvent] = useState(null);
  const [opsEvent, setOpsEvent] = useState(null);
  const [accrEvent, setAccrEvent] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, ib] = await Promise.all([
        axios.get('/api/mymouv/organizer/events'),
        axios.get('/api/mymouv/organizer/iban'),
      ]);
      setEvents(ev.data?.events || []);
      setIban(ib.data?.iban || '');
      setIbanHolder(ib.data?.holder_name || '');
    } catch (_e) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveIban = async () => {
    if (!iban.trim()) { toast.error('Renseignez l\'IBAN'); return; }
    setIbanBusy(true);
    try {
      await axios.post('/api/mymouv/organizer/iban', { iban, holder_name: ibanHolder });
      toast.success('IBAN enregistré');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setIbanBusy(false); }
  };

  const openCreate = () => { setForm(blankEvent()); setEditing('new'); };
  const openEdit = (ev) => { setForm({ ...blankEvent(), ...ev, socials: ev.socials || {}, location: ev.location || { address: '' } }); setEditing(ev.id); };
  const closeForm = () => { setEditing(null); setForm(blankEvent()); };

  const uploadFile = async (file, target) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post('/api/mymouv/organizer/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (target === 'cover') setForm(f => ({ ...f, cover_image_url: r.data.url }));
      else if (target === 'video') setForm(f => ({ ...f, video_url: r.data.url }));
      else setForm(f => ({ ...f, images: [...(f.images || []), r.data.url].slice(0, 3) }));
      toast.success('Fichier importé');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur upload'); }
    finally { setUploading(false); }
  };

  const geocode = async () => {
    const q = form.location?.address?.trim();
    if (!q) return;
    try {
      const r = await axios.get(`/api/mymouv/geocode?q=${encodeURIComponent(q)}`);
      const hit = r.data;
      if (hit?.lat) {
        setForm(f => ({ ...f, location: { ...f.location, lat: hit.lat, lng: hit.lng, name: f.location.name || (hit.display_name?.split(',')[0]) } }));
        toast.success('Adresse localisée');
      } else toast.error('Adresse introuvable');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur de géocodage'); }
  };

  const save = async () => {
    if (!form.title.trim() || !form.event_date || !form.location?.address) {
      toast.error('Titre, date et adresse requis'); return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post('/api/mymouv/organizer/events', form);
        toast.success('Événement créé');
      } else {
        await axios.put(`/api/mymouv/organizer/events/${editing}`, form);
        toast.success('Événement mis à jour');
      }
      window.dispatchEvent(new CustomEvent('mymouv:changed'));
      closeForm();
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Supprimer « ${title} » ?`)) return;
    try {
      await axios.delete(`/api/mymouv/organizer/events/${id}`);
      toast.success('Supprimé');
      window.dispatchEvent(new CustomEvent('mymouv:changed'));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const closeSale = async (id, title) => {
    if (!window.confirm(`Clôturer la vente pour « ${title} » ? Les admins seront notifiés pour valider ton versement.`)) return;
    try {
      await axios.post(`/api/mymouv/organizer/events/${id}/close-sale`);
      toast.success('Vente clôturée. Ton versement sera validé par un admin.');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const cancelEvent = async (id, title) => {
    const reason = window.prompt(`Annuler « ${title} » ? Tous les acheteurs seront prévenus et pourront demander un remboursement.\n\nMotif (obligatoire) :`);
    if (!reason || !reason.trim()) return;
    if (!window.confirm(`Confirmer l'annulation ?\n\nMotif : ${reason}`)) return;
    try {
      await axios.post(`/api/mymouv/organizer/events/${id}/cancel`, { reason: reason.trim() });
      toast.success('Événement annulé. Les acheteurs ont été notifiés.');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const downloadCsv = async (id, title) => {
    try {
      const r = await axios.get(`/api/admin/mymouv/events/${id}/export.csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${title.replace(/\s+/g, '_')}-billets.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur export'); }
  };

  return (
    <div className="space-y-6" data-testid="mymouv-organizer-panel">
      {/* IBAN + Scanner buttons */}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex-1">
            <label className="block text-xs text-white/60 mb-1">IBAN pour les versements MyMouv</label>
            <input value={iban} onChange={e => setIban(e.target.value)} placeholder="FRXX XXXX XXXX XXXX XXXX XXXX XXX" className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-white text-sm" data-testid="mymouv-org-iban" />
          </div>
          <div className="md:w-64">
            <label className="block text-xs text-white/60 mb-1">Nom du titulaire</label>
            <input value={ibanHolder} onChange={e => setIbanHolder(e.target.value)} placeholder="Prénom NOM" className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-white text-sm" data-testid="mymouv-org-iban-holder" />
          </div>
          <button onClick={saveIban} disabled={ibanBusy} className="px-4 py-2 bg-fuchsia-500 hover:bg-fuchsia-600 rounded-lg text-sm font-semibold disabled:opacity-50" data-testid="mymouv-org-iban-save">
            {ibanBusy ? '…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {/* Commission model — collapsible explainer */}
      <div className="rounded-2xl bg-gradient-to-br from-fuchsia-500/10 to-purple-500/10 border border-fuchsia-400/25 p-4" data-testid="mymouv-commission-card">
        <button type="button" onClick={() => setFeesOpen(v => !v)} className="w-full flex items-center justify-between gap-3 text-left" data-testid="mymouv-commission-toggle">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-fuchsia-300" />
            <span className="text-sm font-semibold text-fuchsia-100">{t('commission.title', 'Modèle de commission MyMouv')}</span>
          </div>
          <ChevronRight className={`w-4 h-4 text-fuchsia-200 transition-transform ${feesOpen ? 'rotate-90' : ''}`} />
        </button>
        {feesOpen && (
          <div className="mt-3 space-y-3 text-sm" data-testid="mymouv-commission-details">
            <ul className="space-y-1.5 text-fuchsia-50">
              <li>• {t('commission.bullet1', 'Frais Stripe — 1,5 % + 0,25 € par transaction')}</li>
              <li>• {t('commission.bullet2', 'Commission Destine — 0,75 € par billet vendu')}</li>
              <li>• {t('commission.bullet3', "Le reste vous est reversé sur votre IBAN après validation admin.")}</li>
            </ul>
            <div className="rounded-xl bg-black/30 border border-white/10 p-3">
              <div className="text-xs text-white/60 mb-2 uppercase tracking-wide">{t('commission.exampleTitle', 'Exemple pour un billet à 10 €')}</div>
              <div className="grid grid-cols-2 gap-y-1.5 text-sm">
                <span className="text-white/70">{t('commission.paidByFan', 'Prix payé par le fan')}</span><span className="text-right font-mono">10,00 €</span>
                <span className="text-white/70">{t('commission.stripeFee', 'Frais Stripe (1,5 % + 0,25 €)')}</span><span className="text-right font-mono text-orange-300">−0,40 €</span>
                <span className="text-white/70">{t('commission.destineFee', 'Commission Destine (0,75 €)')}</span><span className="text-right font-mono text-orange-300">−0,75 €</span>
                <span className="pt-1.5 border-t border-white/10 col-span-2"></span>
                <span className="font-semibold text-emerald-200">{t('commission.netToOrg', "Net reversé à l'organisateur")}</span><span className="text-right font-mono font-bold text-emerald-300">8,85 €</span>
              </div>
            </div>
            <p className="text-[11px] text-white/50">{t('commission.promoFree', 'Les billets gratuits (codes promo, invitations) ne génèrent aucun frais.')}</p>
            <div className="rounded-xl bg-amber-500/10 border border-amber-400/30 p-3 mt-1" data-testid="mymouv-commission-reverse-hint">
              <p className="text-[11px] text-amber-100 leading-relaxed">
                <span className="inline-flex items-center gap-1 font-semibold mr-1"><Info className="w-3 h-3" /></span>
                {t('commission.reverseHint', "Envie de recevoir un montant net précis ? Calculez à l'envers : prix du billet = (net souhaité + 1,00 €) ÷ 0,985. Exemple : pour toucher 10 € net, fixez le billet à ~11,17 € (10 € + commissions Stripe + Destine).")}
              </p>
            </div>
            {/* Calendrier des versements — 50 % 1 semaine avant + 100 % le jour J */}
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3 mt-2" data-testid="mymouv-payout-schedule">
              <div className="text-xs font-semibold text-emerald-200 uppercase tracking-wider mb-1.5">
                {t('commission.payoutScheduleTitle', 'Calendrier des versements')}
              </div>
              <ul className="space-y-1 text-[12px] text-emerald-50 leading-relaxed">
                <li>• {t('commission.payoutSchedule1', '50 % du montant net est versé 1 semaine avant le jour de l\u2019événement.')}</li>
                <li>• {t('commission.payoutSchedule2', 'Les 100 % (solde) sont déclenchés le jour même de l\u2019événement.')}</li>
              </ul>
              <p className="text-[10px] text-emerald-100/70 mt-1.5 italic">
                {t('commission.payoutScheduleHint', 'La réception effective des fonds dépend des délais de votre banque.')}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">Mes événements ({events.length})</h3>
        <div className="flex gap-2">
          <button onClick={() => navigate('/mymouv/scanner')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm flex items-center gap-1.5" data-testid="mymouv-org-scanner-btn">
            <QrCode className="w-4 h-4" /> Scanner
          </button>
          <button onClick={openCreate} className="px-4 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-600 hover:to-purple-700 text-sm font-semibold flex items-center gap-1.5" data-testid="mymouv-org-create-btn">
            <Plus className="w-4 h-4" /> Créer un événement
          </button>
        </div>
      </div>

      {/* v49.4 — Payout day banner (13 languages) */}
      <div className="rounded-2xl bg-gradient-to-r from-emerald-500/15 to-cyan-500/15 border border-emerald-500/30 p-3 flex items-center gap-2 text-emerald-100 text-sm" data-testid="mymouv-payout-banner">
        <CalendarDays className="w-4 h-4 shrink-0" />
        <span>{opL.payout}</span>
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
      ) : events.length === 0 ? (
        <div className="text-center py-12 text-white/40 border border-dashed border-white/10 rounded-2xl">
          <Ticket className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>Aucun événement pour l&apos;instant.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map(ev => (
            <div key={ev.id} className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid={`mymouv-org-event-${ev.id}`}>
              <div className="flex items-start gap-4">
                {ev.cover_image_url && <img src={getMediaUrl(ev.cover_image_url)} alt="" className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h4 className="font-bold">{ev.title} <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${ev.status === 'published' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-white/60'}`}>{ev.status === 'published' ? 'Publié' : 'Brouillon'}</span></h4>
                      <div className="text-xs text-white/60 mt-1 flex items-center gap-2 flex-wrap">
                        <span>{ev.event_date} · {ev.event_time}</span>
                        <span><MapPin className="w-3 h-3 inline" /> {ev.location?.name || ev.location?.address}</span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(ev)} className="p-1.5 rounded-lg hover:bg-white/10" title="Modifier"><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => remove(ev.id, ev.title)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-300" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>

                  {/* Revenue + payout status */}
                  {ev.revenue && (
                    <div className="mt-3 p-3 rounded-lg bg-black/30 grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs">
                      <div>
                        <div className="text-white/50">Billets vendus</div>
                        <div className="font-bold text-white">{ev.tickets_sold}</div>
                      </div>
                      <div>
                        <div className="text-white/50">Recette brute</div>
                        <div className="font-bold text-white">{(ev.revenue.gross_cents / 100).toFixed(2)} €</div>
                      </div>
                      <div>
                        <div className="text-white/50">Frais Stripe + Destine</div>
                        <div className="font-bold text-orange-300">-{((ev.revenue.stripe_fee_cents + ev.revenue.platform_fee_cents) / 100).toFixed(2)} €</div>
                      </div>
                      <div>
                        <div className="text-white/50">Net à recevoir</div>
                        <div className="font-bold text-emerald-300">{(ev.revenue.net_cents / 100).toFixed(2)} €</div>
                      </div>
                      <div data-testid={`mymouv-org-promo-stat-${ev.id}`}>
                        <div className="text-white/50">Billets code promo</div>
                        <div className="font-bold text-fuchsia-300">{ev.promo_tickets || 0}<span className="text-white/40 text-[11px] font-normal"> / {ev.promo_codes_used || 0} utilisés</span></div>
                      </div>
                    </div>
                  )}

                  {/* Actions bar */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button onClick={() => setPromoEvent(ev)} className="px-3 py-1.5 rounded-lg bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 text-xs flex items-center gap-1" data-testid={`mymouv-org-promo-btn-${ev.id}`}>
                      <Tag className="w-3.5 h-3.5" /> Codes promo
                    </button>
                    <button onClick={() => setOpsEvent(ev)} className="px-3 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 text-xs flex items-center gap-1" data-testid={`mymouv-org-ops-btn-${ev.id}`}>
                      <Briefcase className="w-3.5 h-3.5" /> {opL.ops}
                    </button>
                    {ev.allow_accreditations && (
                      <button onClick={() => setAccrEvent(ev)} className="px-3 py-1.5 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-200 text-xs flex items-center gap-1" data-testid={`mymouv-org-accr-btn-${ev.id}`}>
                        <ShieldCheck className="w-3.5 h-3.5" /> {opL.accr}
                      </button>
                    )}
                    {ev.status !== 'cancelled' && (
                      <button onClick={() => cancelEvent(ev.id, ev.title)} className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 text-xs flex items-center gap-1" data-testid={`mymouv-org-cancel-btn-${ev.id}`}>
                        <XCircle className="w-3.5 h-3.5" /> Annuler l&apos;événement
                      </button>
                    )}
                    {ev.status === 'cancelled' && (
                      <span className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-300 text-xs flex items-center gap-1" data-testid={`mymouv-org-cancelled-${ev.id}`}>
                        <XCircle className="w-3.5 h-3.5" /> Annulé
                      </span>
                    )}
                    {ev.payout?.status === 'validated' ? (
                      <span className="px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-300 text-xs flex items-center gap-1" data-testid={`mymouv-org-payout-validated-${ev.id}`}>
                        <CheckCircle className="w-3.5 h-3.5" /> Versement validé — email envoyé
                      </span>
                    ) : ev.sale_closed ? (
                      <span className="px-3 py-1.5 rounded-lg bg-yellow-500/20 text-yellow-300 text-xs">Vente clôturée — en attente admin</span>
                    ) : ev.tickets_sold > 0 && (
                      <button onClick={() => closeSale(ev.id, ev.title)} className="px-3 py-1.5 rounded-lg bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 text-xs flex items-center gap-1" data-testid={`mymouv-org-close-sale-${ev.id}`}>
                        <CheckCircle className="w-3.5 h-3.5" /> Clôturer la vente
                      </button>
                    )}
                    {ev.tickets_sold > 0 && (
                      <button onClick={() => downloadCsv(ev.id, ev.title)} className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs flex items-center gap-1" data-testid={`mymouv-org-csv-${ev.id}`}>
                        <Download className="w-3.5 h-3.5" /> Export CSV
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit form modal */}
      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
          <div className="my-6 max-w-2xl w-full bg-gray-900 rounded-3xl border border-white/10 p-6" data-testid="mymouv-org-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{editing === 'new' ? t('mymouvForm.titleNew', 'Nouvel événement') : t('mymouvForm.titleEdit', "Modifier l'événement")}</h3>
              <button onClick={closeForm} className="p-2 rounded-full hover:bg-white/10"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              {/* Cover */}
              <div>
                <label className="block text-xs text-white/60 mb-1">{t('mymouvForm.coverLabel', 'Image de couverture')} <span className="text-white/40">({t('mymouvForm.coverHint', 'recommandé 1920×1080 px, ratio 16:9, JPG/PNG/WebP, max 15 Mo')})</span></label>
                {form.cover_image_url && (
                  <div className="mb-2 relative">
                    <img src={getMediaUrl(form.cover_image_url)} alt="" className="w-full aspect-video object-cover rounded-xl" />
                    <button type="button" onClick={() => setForm(f => ({ ...f, cover_image_url: '' }))} className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-black/80 rounded-full"><X className="w-4 h-4" /></button>
                  </div>
                )}
                <button type="button" onClick={() => coverInputRef.current?.click()} disabled={uploading} className="w-full py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="mymouv-org-cover-upload">
                  <Upload className="w-4 h-4" /> {t('mymouvForm.importCover', 'Importer la couverture')}
                </button>
                <input ref={coverInputRef} type="file" accept="image/*" onChange={e => uploadFile(e.target.files?.[0], 'cover')} className="hidden" />
              </div>

              {/* Title / description */}
              <div>
                <label className="block text-xs text-white/60 mb-1">{t('mymouvForm.titleField', 'Titre *')}</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="mymouv-org-title" />
              </div>
              <div>
                <label className="block text-xs text-white/60 mb-1">{t('mymouvForm.descField', 'Description')}</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
              </div>

              {/* Date / time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-white/60 mb-1">{t('mymouvForm.dateField', 'Date *')}</label>
                  <input type="date" value={form.event_date} onChange={e => setForm({ ...form, event_date: e.target.value })} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="mymouv-org-date" />
                </div>
                <div>
                  <label className="block text-xs text-white/60 mb-1">{t('mymouvForm.timeField', 'Heure')}</label>
                  <input type="time" value={form.event_time} onChange={e => setForm({ ...form, event_time: e.target.value })} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>

              {/* Location */}
              <div className="space-y-2 p-3 rounded-xl bg-white/5">
                <label className="block text-xs text-white/60">{t('event.location', 'Lieu')}</label>
                <input value={form.location.name} onChange={e => setForm({ ...form, location: { ...form.location, name: e.target.value } })} placeholder={t('mymouvForm.placeName', 'Nom du lieu (ex: Olympia)')} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="mymouv-org-loc-name" />
                <div className="flex gap-2">
                  <input value={form.location.address} onChange={e => setForm({ ...form, location: { ...form.location, address: e.target.value } })} placeholder={t('mymouvForm.address', 'Adresse complète *')} className="flex-1 bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="mymouv-org-loc-address" />
                  <button type="button" onClick={geocode} className="px-3 py-2 rounded-lg bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 text-sm">{t('mymouvForm.locate', 'Localiser')}</button>
                </div>
                {form.location.lat && (
                  <div className="text-[11px] text-emerald-300">📍 {form.location.lat.toFixed(4)}, {form.location.lng.toFixed(4)}</div>
                )}
              </div>

              {/* Security */}
              <div>
                <label className="block text-xs text-white/60 mb-1">{t('mymouvForm.securityLabel', 'Mesures de sécurité')}</label>
                <textarea value={form.security_measures} onChange={e => setForm({ ...form, security_measures: e.target.value })} rows={2} placeholder={t('mymouvForm.securityPlaceholder', "Contrôle d'identité, fouille, âge minimum...")} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
              </div>

              {/* Lineup */}
              <div>
                <label className="block text-xs text-white/60 mb-1">{t('mymouvForm.lineupLabel', 'Line-up')}</label>
                {(form.lineup || []).map((a, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input value={a.name} onChange={e => { const l = [...form.lineup]; l[i].name = e.target.value; setForm({ ...form, lineup: l }); }} placeholder={t('mymouvForm.artistName', 'Nom')} className="flex-1 bg-white/10 border border-white/15 rounded-lg px-2 py-1.5 text-sm" />
                    <input value={a.url || ''} onChange={e => { const l = [...form.lineup]; l[i].url = e.target.value; setForm({ ...form, lineup: l }); }} placeholder={t('mymouvForm.artistLink', 'Lien (Wikipédia/réseau)')} className="flex-1 bg-white/10 border border-white/15 rounded-lg px-2 py-1.5 text-sm" />
                    <button type="button" onClick={() => setForm({ ...form, lineup: form.lineup.filter((_, j) => j !== i) })} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-300"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <button type="button" onClick={() => setForm({ ...form, lineup: [...(form.lineup || []), { name: '', url: '' }] })} className="text-xs text-fuchsia-300 hover:text-fuchsia-200">+ {t('mymouvForm.addArtist', 'Ajouter un artiste')}</button>
              </div>

              {/* Ticket types */}
              <div className="space-y-2">
                <label className="block text-xs text-white/60">{t('mymouvForm.ticketTypes', 'Types de billets')}</label>
                {(form.ticket_types || []).map((tt, i) => (
                  <div key={tt.id} className="p-3 rounded-lg bg-white/5 grid grid-cols-2 md:grid-cols-5 gap-2" data-testid={`mymouv-org-ticket-row-${i}`}>
                    <input value={tt.name} onChange={e => { const arr = [...form.ticket_types]; arr[i].name = e.target.value; setForm({ ...form, ticket_types: arr }); }} placeholder={t('mymouvForm.ticketName', 'Nom (ex: Standard)')} className="bg-white/10 border border-white/15 rounded px-2 py-1.5 text-sm" />
                    <input type="number" min="0" step="0.01" value={tt.price} onChange={e => { const arr = [...form.ticket_types]; arr[i].price = e.target.value; setForm({ ...form, ticket_types: arr }); }} placeholder={t('mymouvForm.ticketPrice', 'Prix')} className="bg-white/10 border border-white/15 rounded px-2 py-1.5 text-sm" />
                    <select value={tt.currency} onChange={e => { const arr = [...form.ticket_types]; arr[i].currency = e.target.value; setForm({ ...form, ticket_types: arr }); }} className="bg-white/10 border border-white/15 rounded px-2 py-1.5 text-sm">
                      {CURRENCIES.map(c => <option key={c} value={c} className="bg-gray-900">{c}</option>)}
                    </select>
                    <input type="number" min="1" value={tt.quantity} onChange={e => { const arr = [...form.ticket_types]; arr[i].quantity = parseInt(e.target.value) || 1; setForm({ ...form, ticket_types: arr }); }} placeholder={t('mymouvForm.ticketQty', 'Quantité')} className="bg-white/10 border border-white/15 rounded px-2 py-1.5 text-sm" />
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1 text-xs">
                        <input type="checkbox" checked={tt.is_vip} onChange={e => { const arr = [...form.ticket_types]; arr[i].is_vip = e.target.checked; setForm({ ...form, ticket_types: arr }); }} /> {t('mymouvForm.ticketVIP', 'VIP')}
                      </label>
                      <button type="button" onClick={() => setForm({ ...form, ticket_types: form.ticket_types.filter((_, j) => j !== i) })} className="p-1 rounded hover:bg-red-500/20 text-red-300"><X className="w-3 h-3" /></button>
                    </div>
                  </div>
                ))}
                <button type="button" onClick={() => setForm({ ...form, ticket_types: [...form.ticket_types, blankTicket()] })} className="text-xs text-fuchsia-300">+ {t('mymouvForm.addTicketType', 'Ajouter un type de billet')}</button>
              </div>

              {/* External link + socials */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input value={form.external_link_title} onChange={e => setForm({ ...form, external_link_title: e.target.value })} placeholder={t('mymouvForm.externalLinkTitle', 'Titre du lien externe')} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
                <input value={form.external_link_url} onChange={e => setForm({ ...form, external_link_url: e.target.value })} placeholder={t('mymouvForm.externalLinkUrl', 'URL du lien')} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
                <input value={form.socials.instagram} onChange={e => setForm({ ...form, socials: { ...form.socials, instagram: e.target.value } })} placeholder="Instagram" className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
                <input value={form.socials.facebook} onChange={e => setForm({ ...form, socials: { ...form.socials, facebook: e.target.value } })} placeholder="Facebook" className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
                <input value={form.socials.twitter} onChange={e => setForm({ ...form, socials: { ...form.socials, twitter: e.target.value } })} placeholder="Twitter/X" className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
                <input value={form.socials.tiktok} onChange={e => setForm({ ...form, socials: { ...form.socials, tiktok: e.target.value } })} placeholder="TikTok" className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
              </div>

              {/* Extra media */}
              <div className="flex gap-2 flex-wrap">
                <button type="button" onClick={() => galleryInputRef.current?.click()} disabled={uploading || (form.images?.length || 0) >= 3} className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs disabled:opacity-50">+ {t('mymouvForm.addImages', 'Image')} ({form.images?.length || 0}/3)</button>
                <input ref={galleryInputRef} type="file" accept="image/*" onChange={e => uploadFile(e.target.files?.[0], 'gallery')} className="hidden" />
                <button type="button" onClick={() => videoInputRef.current?.click()} disabled={uploading} className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs disabled:opacity-50">{form.video_url ? `✓ ${t('mymouvForm.videoDone', 'Vidéo')}` : `+ ${t('mymouvForm.addVideo', 'Vidéo (< 60s)')}`}</button>
                <input ref={videoInputRef} type="file" accept="video/*" onChange={e => uploadFile(e.target.files?.[0], 'video')} className="hidden" />
              </div>

              {/* Status */}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.status === 'published'} onChange={e => setForm({ ...form, status: e.target.checked ? 'published' : 'draft' })} data-testid="mymouv-org-publish" />
                {t('mymouvForm.publishNow', 'Publier immédiatement')}
              </label>

              {/* v49.4 — Allow accreditation requests */}
              <label className="flex items-center gap-2 text-sm" data-testid="mymouv-org-allow-accr-label">
                <input type="checkbox" checked={!!form.allow_accreditations} onChange={e => setForm({ ...form, allow_accreditations: e.target.checked })} data-testid="mymouv-org-allow-accr" />
                <ShieldCheck className="w-4 h-4 text-cyan-300" /> {opL.allowAccr}
              </label>

              <div className="flex gap-2 justify-end pt-2 border-t border-white/10">
                <button onClick={closeForm} className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm">{t('mymouvForm.cancelBtn', 'Annuler')}</button>
                <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-600 disabled:opacity-50 text-sm font-semibold flex items-center gap-1.5" data-testid="mymouv-org-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? t('mymouvForm.savingBtn', 'Enregistrement…') : t('mymouvForm.save', 'Enregistrer')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Promo codes panel */}
      {promoEvent && (
        <MyMouvPromoCodesPanel event={promoEvent} onClose={() => { setPromoEvent(null); load(); }} />
      )}

      {/* v49.4 — Event operations (private) */}
      {opsEvent && (
        <EventOperationsModal
          eventId={opsEvent.id}
          eventTitle={opsEvent.title}
          onClose={() => setOpsEvent(null)}
        />
      )}

      {/* v49.4 — Accreditations inbox */}
      {accrEvent && (
        <EventAccreditationsInbox
          eventId={accrEvent.id}
          eventTitle={accrEvent.title}
          onClose={() => { setAccrEvent(null); load(); }}
        />
      )}
    </div>
  );
};

export default MyMouvOrganizerPanel;
